import {
  ShoppingCart,
  Star,
  Share2,
  MessageCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { SocialShareButton } from '../ShareButton';
import { PaymentMethodsModal } from '../PaymentMethodsModal';
import { ShippingDetailsModal } from '../ShippingDetailsModal';

interface ProductInfoProps {
  products: any;
  quantity: number;
  selectedColor: any;
  selectedSize: any;
  addCart: () => void;
  quantityChange: (increment: boolean) => void;
  user: any;
  shippingCost: string | null;
  handleColorSelect: (color: string, image: string, id: string) => void;
  handleSizeSelect: (size: string, id: string) => void;
  onChat: (sellerId: string) => void;
  averageStars: number;
  totalReviews: number;
}

export const ProductInfo: React.FC<ProductInfoProps> = ({
  products,
  onChat,
  quantity,
  selectedColor,
  selectedSize,
  addCart,
  quantityChange,
  user,
  shippingCost,
  handleColorSelect,
  handleSizeSelect,
  averageStars,
  totalReviews,
}) => {
  const toCurrency = (value: number) => {
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  };

  const isUniqueProduct = () => {
    return (
      products.grade?.length === 1 &&
      products.grade[0].color === 'UNICO' &&
      products.grade[0].name === null
    );
  };

  const getEstimatedDelivery = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 10);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 14);

    const formatDate = (date: Date) => {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      return `${day}/${month}`;
    };

    return `${formatDate(minDate)} - ${formatDate(maxDate)}`;
  };

  const mainAddress =
    user?.address?.find((addr: any) => addr.main === true) ??
    user?.address?.[0];
  const firstGrade = products.grade?.[0];
  const firstPrice = firstGrade?.gradeSizes?.[0]?.price;
  const basePrice = firstPrice?.price ?? 0;
  const discountPercent = firstPrice?.discount ?? null;

  const imageUrl = firstGrade?.image
    ? `${process.env.REACT_APP_BASE_URL}/uploads/${firstGrade.image}`
    : undefined;

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const getSelectedSizeDetails = () => {
    if (!selectedColor?.color || !selectedSize?.size) return null;
    const selectedGrade = products.grade?.find(
      (grade: any) => grade.color === selectedColor.color
    );
    const selectedSizeData = selectedGrade?.gradeSizes?.find(
      (size: any) => size.size === selectedSize.size
    );
    return selectedSizeData;
  };

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
  const selectedSizeDetails = getSelectedSizeDetails();
  const displayPrice = selectedSizeDetails?.price?.price ?? basePrice;
  const displayDiscount =
    selectedSizeDetails?.price?.discount ?? discountPercent;
  const hasValidDiscount = displayDiscount && displayDiscount > 0;
  const finalPrice = hasValidDiscount
    ? displayPrice - (displayPrice * displayDiscount) / 100
    : displayPrice;

  const handleBuy = async () => {
    if (products && quantity > 0) {
      const isUnique = products.grade?.some(
        (grade: any) => grade?.name === null && grade?.color === 'UNICO'
      );

      const isGradeSelect = !isUnique;

      if (isGradeSelect && !selectedColor?.color) {
        alert('Selecione uma cor');
        return;
      }
      if (isGradeSelect && !selectedSize?.size) {
        alert('Selecione uma variação');
        return;
      }
      if (quantity <= 0) {
        alert('Selecione a quantidade');
        return;
      }
      await addCart();
      window.location.href = '/checkout';
    } else {
      alert('Por favor, selecione um item válido.');
    }
  };
  return (
    <div className="w-full bg-white sm:pb-4">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2 sm:mb-1">
          <h1 className="text-2xl font-semibold pr-4 hidden sm:block">
            {products.name}
          </h1>
          <span className="cursor-pointer text-gray-400 text-xs whitespace-nowrap hidden sm:block">
            Denunciar
          </span>
        </div>
        {products.totalSale > 0 && (
          <p className="text-xs text-gray-500 mb-3 hidden sm:block">
            Novidade | +{products.totalSale} vendidos
          </p>
        )}

        <div className="flex items-center justify-between mb-3 hidden sm:flex">
          <p className="text-xs text-gray-600">
            Vendido por:{' '}
            {products.storeSeller ? (
              <a
                href={`/store/${products.storeSeller?.id}`}
                className="font-semibold text-blue-600"
              >
                {products.storeSeller?.name}
              </a>
            ) : (
              <span className="font-semibold">Loja</span>
            )}
          </p>
          {totalReviews > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-600">
                {averageStars.toFixed(2)} ({totalReviews}{' '}
                {totalReviews === 1 ? 'avaliação' : 'avaliações'})
              </span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={14}
                    className={`${star <= Math.round(averageStars) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-300 text-gray-300'}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="md:hidden mb-3 flex items-center gap-2">
          <div className="flex items-center gap-2 flex-1">
            <h3 className="text-base font-semibold">Variações disponíveis</h3>

            {products.totalSale && products.totalSale > 0 && (
              <span className="text-xs text-gray-500">
                Novidade | +{products.totalSale} vendidos
              </span>
            )}
          </div>

          <SocialShareButton
            productName={products.name}
            productPrice={toCurrency(displayPrice)}
            productImage={imageUrl}
            productUrl={pageUrl}
          />
        </div>

        <div className="mb-3 hidden sm:block">
          {products.promotion !== false && hasValidDiscount ? (
            <>
              <div className="text-sm text-gray-400 line-through mb-0.5">
                {toCurrency(displayPrice)}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold">
                  R$ {Math.floor(finalPrice).toLocaleString('pt-BR')}
                  <sup className="text-sm">
                    ,{(finalPrice % 1).toFixed(2).split('.')[1]}
                  </sup>
                </span>
                <span className="bg-green-100 text-green-600 text-xs font-semibold px-2 py-0.5 rounded">
                  {displayDiscount}%OFF
                </span>
              </div>
            </>
          ) : (
            <div className="text-2xl font-bold">
              R$ {Math.floor(displayPrice).toLocaleString('pt-BR')}
              <sup className="text-sm">
                ,{(displayPrice % 1).toFixed(2).split('.')[1]}
              </sup>
            </div>
          )}
        </div>

        <button
          onClick={() => setIsPaymentModalOpen(true)}
          className="text-sm text-blue-600 hover:underline mb-3 hidden sm:block"
        >
          Ver os meios de pagamento
        </button>

        {products.grade?.length && !isUniqueProduct() ? (
          <div className="mb-3">
            <div className="mb-2">
              <span className="text-sm">
                Cor:{' '}
                <span className="text-red-500">
                  {selectedColor?.color || products.grade[0]?.color}
                </span>
              </span>
            </div>

            <div className="flex gap-2 overflow-x-auto">
              {products.grade?.map((grade: any) => (
                <button
                  key={grade.id ?? `${grade.color}-${grade.image}`}
                  onClick={() =>
                    handleColorSelect(
                      grade.color || '',
                      grade.image
                        ? `${process.env.REACT_APP_BASE_URL}/uploads/${grade.image}`
                        : '',
                      grade.id ? grade.id : ''
                    )
                  }
                  className={`relative w-14 h-14 flex-shrink-0 border rounded ${selectedColor?.color === grade.color ? 'border-red-500 border-2' : 'border-gray-300'}`}
                  style={{
                    backgroundImage: grade.image
                      ? `url(${process.env.REACT_APP_BASE_URL}/uploads/${grade.image})`
                      : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {!grade.image && (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-[10px]">
                      {grade.color}
                    </div>
                  )}
                  {selectedColor?.color === grade.color && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {selectedColor?.color &&
          !isUniqueProduct() &&
          (products.grade?.find(
            (grade: any) => grade.color === selectedColor?.color
          )?.gradeSizes?.length ?? 0) > 1 && (
            <div className="mt-3 pt-3 border-t">
              <div className="mb-2">
                <span className="text-sm text-gray-600">Variação:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {products.grade
                  ?.find((grade: any) => grade.color === selectedColor?.color)
                  ?.gradeSizes?.map((size: any) => {
                    const isSelected = selectedSize?.size === size.size;
                    const isOutOfStock = Number.parseInt(size.stock) === 0;
                    return (
                      <button
                        key={size.id ?? size.size}
                        onClick={() =>
                          !isOutOfStock &&
                          handleSizeSelect(size.size, size.id ? size.id : '')
                        }
                        disabled={isOutOfStock}
                        className={`px-3 py-1.5 border rounded text-sm ${isSelected ? 'border-red-500 bg-red-50 text-red-500' : isOutOfStock ? 'border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-300 text-gray-700'}`}
                      >
                        {size.size}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

        <div className="mt-2 mb-2 sm:hidden">
          {products.promotion !== false && hasValidDiscount ? (
            <>
              <div className="text-sm text-gray-400 line-through">
                {toCurrency(displayPrice)}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-[32px] font-bold leading-none">
                  R$ {Math.floor(finalPrice).toLocaleString('pt-BR')}
                  <sup className="text-sm">
                    ,{(finalPrice % 1).toFixed(2).split('.')[1]}
                  </sup>
                </span>
                <span className="bg-green-100 text-green-600 text-sm font-bold px-2 py-0.5 rounded">
                  {displayDiscount}%OFF
                </span>
              </div>
            </>
          ) : (
            <div className="text-[32px] font-bold leading-none">
              R$ {Math.floor(displayPrice).toLocaleString('pt-BR')}
              <sup className="text-sm">
                ,{(displayPrice % 1).toFixed(2).split('.')[1]}
              </sup>
            </div>
          )}
        </div>
        <button
          onClick={() => setIsPaymentModalOpen(true)}
          className="text-sm text-blue-600 hover:underline mb-2 sm:hidden"
        >
          Ver os meios de pagamento
        </button>

        <h2 className="text-base mb-3 sm:hidden">{products.name}</h2>

        <div className="mt-2 bg-green-50 rounded p-2.5 mb-3 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-green-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
          </svg>
          <div className="flex-1 min-w-0">
            {products.freeShipping ? (
              <>
                <div className="text-green-600 font-bold text-sm">
                  Frete grátis
                </div>
                <div className="text-xs text-gray-600">
                  {mainAddress?.city === products.storeSeller?.city
                    ? 'Entrega em até 2 horas após a compra'
                    : 'Prazo após confirmação'}
                </div>
              </>
            ) : mainAddress?.city &&
              products.storeSeller?.city &&
              mainAddress?.city === products.storeSeller?.city ? (
              <>
                <div className="text-green-600 font-bold text-sm">
                  Frete grátis Local
                </div>
                <div className="text-xs text-gray-600">
                  Entrega em até 2 horas após a compra
                </div>
              </>
            ) : mainAddress ? (
              <>
                <div className="text-green-600 font-bold text-sm">
                  {shippingCost || 'Calcular frete'}
                </div>
                <div className="text-xs text-gray-600">
                  Prazo após confirmação
                </div>
              </>
            ) : (
              <>
                <div className="text-gray-600 font-bold text-sm">Frete</div>
                <div className="text-xs text-gray-600">
                  Entrega entre {getEstimatedDelivery()}
                </div>
              </>
            )}
          </div>
          <svg
            className="w-4 h-4 text-gray-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>

        <button
          onClick={() => setIsShippingModalOpen(true)}
          className="text-sm text-blue-600 hover:underline mb-3"
        >
          Mais detalhes e forma de entrega
        </button>

        <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
          <svg
            className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-green-700">
                Compra Garantida.
              </span>{' '}
              Receba o produto que está esperando ou devolvemos o dinheiro.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4 pb-3 border-b hidden sm:flex">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Quantidade:</span>
            <div className="flex items-center border border-gray-300 rounded">
              <button
                className="w-7 h-7 flex items-center justify-center text-gray-600"
                onClick={() => quantityChange(false)}
              >
                −
              </button>
              <span className="px-3 text-sm font-medium border-x border-gray-300">
                {quantity}
              </span>
              <button
                className="w-7 h-7 flex items-center justify-center text-gray-600"
                onClick={() => quantityChange(true)}
              >
                +
              </button>
            </div>
            <span className="text-xs text-gray-500">
              {selectedSizeDetails?.stock || products.stock} unidades
              disponíveis
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between py-2.5 border-t sm:hidden">
          <div className="text-sm">
            Vendido por:{' '}
            {products.storeSeller ? (
              <a
                href={`/store/${products.storeSeller?.id}`}
                className="font-semibold text-blue-600"
              >
                {products.storeSeller?.name}
              </a>
            ) : (
              <span className="font-semibold">Loja</span>
            )}
          </div>
          {totalReviews > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600">
                {averageStars.toFixed(2)} ({totalReviews}{' '}
                {totalReviews === 1 ? 'avaliação' : 'avaliações'})
              </span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={14}
                    className={`${star <= Math.round(averageStars) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-300 text-gray-300'}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-3 sm:hidden">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Quantidade:</span>
            <div className="flex items-center border border-gray-300 rounded">
              <button
                className="w-7 h-7 flex items-center justify-center"
                onClick={() => quantityChange(false)}
              >
                −
              </button>
              <span className="px-3 text-sm font-medium border-x">
                {quantity}
              </span>
              <button
                className="w-7 h-7 flex items-center justify-center"
                onClick={() => quantityChange(true)}
              >
                +
              </button>
            </div>
            <span className="text-xs text-gray-500">
              {selectedSizeDetails?.stock || products.stock} unidades
              disponíveis
            </span>
          </div>
        </div>
      </div>

      <div className="sm:relative sm:mt-4 fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg sm:shadow-none z-50">
        <div className="flex items-stretch gap-0 h-16 sm:h-auto sm:items-center sm:gap-2 sm:px-3 sm:py-2">
          <button
            onClick={() => onChat(products.storeSeller?.id as string)}
            className="flex-1 flex flex-col items-center justify-center gap-1 border-r border-gray-200 sm:flex-1 sm:py-3 sm:bg-white sm:border sm:border-gray-300 sm:rounded-lg sm:flex-row sm:gap-1.5 hover:bg-gray-50 transition-all active:bg-gray-100"
          >
            <MessageCircle
              size={24}
              className="text-green-700 sm:text-gray-600 sm:w-[18px] sm:h-[18px]"
            />
            <span className="text-xs text-gray-700 font-normal sm:text-sm sm:font-medium">
              Chat
            </span>
          </button>

          <button
            onClick={() => addCart()}
            className="flex-1 flex flex-col items-center justify-center gap-1 border-r border-gray-200 sm:flex-1 sm:py-3 sm:bg-white sm:border sm:border-gray-300 sm:rounded-lg sm:flex-row sm:gap-1.5 hover:bg-gray-50 transition-all active:bg-gray-100"
          >
            <ShoppingCart
              size={24}
              className="text-red-700 sm:text-gray-600 sm:w-[18px] sm:h-[18px]"
            />
            <span className="text-xs text-gray-700 font-normal sm:text-sm sm:font-medium">
              Adicionar
            </span>
          </button>

          <button
            onClick={handleBuy}
            className="flex-1 bg-red-500 text-white rounded-none sm:rounded-lg font-semibold text-sm sm:font-semibold sm:text-sm sm:py-3 hover:bg-red-600 transition-all active:bg-red-700"
          >
            Comprar
          </button>
        </div>
      </div>
      <PaymentMethodsModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />
      <ShippingDetailsModal
        isOpen={isShippingModalOpen}
        onClose={() => setIsShippingModalOpen(false)}
      />
    </div>
  );
};
